import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useWhitelistRole } from '../useWhitelistRole';
import { buildTaskProgressMap, getProjectProgressBreakdown } from '../utils/projectProgress';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, kpiGridMobile, pageMobile, TAP_TARGET, dialog } from '../mobileStyles';
import BottomSheet from './BottomSheet';

// Display a stored E.164 phone (e.g. "+51999888777") as "+51 999 888 777".
// Falls back to the raw value if it isn't a parseable international number.
function formatPhoneDisplay(stored) {
  if (!stored) return '';
  return formatPhoneNumberIntl(stored) || stored;
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h14" />
      <path d="M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2" />
      <path d="M5 6l1 11a2 2 0 002 2h4a2 2 0 002-2l1-11" />
      <line x1="9" y1="10" x2="9" y2="15" />
      <line x1="11" y1="10" x2="11" y2="15" />
    </svg>
  );
}

function getStatusStyle(status) {
  switch (status) {
    case 'En curso':
      return { background: 'rgba(107,28,42,0.08)', color: '#6B1C2A' };
    case 'Completado':
      return { background: 'rgba(74,124,89,0.08)', color: '#4A7C59' };
    case 'Pendiente':
      return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Inicio':
      return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Diseño':
      // Legacy value — render as Pendiente coloring so older rows still look OK.
      return { background: 'rgba(201,169,110,0.12)', color: '#A08040' };
    case 'Planificación':
      return { background: 'rgba(107,107,107,0.08)', color: '#6B6B6B' };
    case 'Pausado':
      return { background: 'rgba(184,59,59,0.08)', color: '#B83B3B' };
    default:
      return { background: '#f0f0f0', color: '#666' };
  }
}

const STATUS_CHOICES = ['En curso', 'Pendiente', 'Pausado', 'Completado'];

// Phase status palette — deliberately different from getStatusStyle() above,
// which colours the project's general estado. Here: Completado green,
// En curso gold, Pausado/Pendiente gray.
const PHASE_STATUS_COLORS = {
  'Completado': { dot: '#4A7C59', background: 'rgba(74,124,89,0.12)',   color: '#4A7C59' },
  'En curso':   { dot: '#C9A96E', background: 'rgba(201,169,110,0.18)', color: '#A08040' },
  'Pausado':    { dot: '#9B9B9B', background: 'rgba(155,155,155,0.14)', color: '#6B6B6B' },
  'Pendiente':  { dot: '#9B9B9B', background: 'rgba(155,155,155,0.14)', color: '#6B6B6B' },
};
const PHASE_STATUS_EMPTY = { dot: '#D6D0C8', background: 'transparent', color: 'var(--text-light)' };

function getPhaseStatusStyle(estado) {
  const key = (estado || '').trim();
  const c = PHASE_STATUS_COLORS[key] || PHASE_STATUS_EMPTY;
  return { background: c.background, color: c.color };
}
function phaseDotColor(estado) {
  const key = (estado || '').trim();
  return (PHASE_STATUS_COLORS[key] || PHASE_STATUS_EMPTY).dot;
}

// Small "📐 ●  🏗️ ●" pair shown under the progress bars in the projects table.
// Phases with no estado set are skipped rather than rendered as empty dots.
function PhaseBadges({ diseno, obra }) {
  const items = [
    { icon: '📐', estado: diseno, label: 'Diseño' },
    { icon: '🏗️', estado: obra, label: 'Obra' },
  ].filter(i => (i.estado || '').trim());
  if (items.length === 0) return null;
  return (
    <div style={styles.phaseBadgeRow}>
      {items.map(i => (
        <span key={i.label} style={styles.phaseBadge} title={`${i.label}: ${i.estado}`}>
          <span>{i.icon}</span>
          <span style={{ ...styles.phaseDot, background: phaseDotColor(i.estado) }} />
        </span>
      ))}
    </div>
  );
}

function getProgressColor(progress) {
  if (progress === 100) return '#4A7C59';
  if (progress >= 60) return '#6B1C2A';
  if (progress >= 30) return '#C9A96E';
  return '#C4922A';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateDMY(dateStr) {
  if (!dateStr) return '';
  const s = String(dateStr).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
}

const emptyForm = {
  nombre: '',
  cliente: '',
  cliente_telefono: '',
  cliente_email: '',
  cliente_dni: '',
  cliente_origen: '',
  tipo: 'Residencial',
  modalidad: 'Ambos',
  inicio: '',
  fin_estimado: '',
  estado: 'En curso',
  arquitecta_id: '',
};

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function MiniAvatar({ member, size = 26 }) {
  const color = member?.color || '#6B1C2A';
  if (member?.foto_url) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: `#fff url(${member.foto_url}) center/cover no-repeat`,
        boxShadow: `0 0 0 2px ${color}22`,
        flexShrink: 0,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
      fontSize: Math.max(10, Math.round(size * 0.42)),
      letterSpacing: '0.02em', flexShrink: 0,
    }}>
      {getInitials(member?.nombre)}
    </div>
  );
}

export default function Dashboard({ onNavigate, prefill, onPrefillConsumed } = {}) {
  const { isAdmin, email: currentEmail } = useWhitelistRole();
  const { isMobile, isTablet, isCompact, isTouch } = useBreakpoint();
  // Compact-only UI state: the "Filtros" sheet and the per-card choice sheet
  // (phase estado / arquitecta) that replaces the table's portal dropdowns.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cardSheet, setCardSheet] = useState(null); // { kind: 'estado'|'arquitecta', projectId, field?, label? }
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  const closeCardSheet = useCallback(() => setCardSheet(null), []);
  const [projects, setProjects] = useState([]);
  const [taskCount, setTaskCount] = useState(0);
  const [taskProgressMap, setTaskProgressMap] = useState({});
  const [sortDir, setSortDir] = useState('desc'); // 'desc' = newest inicio first
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // A lead handed over from the Leads section opens the "Nuevo Proyecto" modal
  // with the client fields already filled in.
  useEffect(() => {
    if (!prefill) return;
    setForm({ ...emptyForm, ...prefill });
    setShowModal(true);
    setShowClientSuggestions(false);
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  const [detailProject, setDetailProject] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [savingField, setSavingField] = useState(null);
  const [savedFlash, setSavedFlash] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusEditId, setStatusEditId] = useState(null);
  const [statusEditField, setStatusEditField] = useState(null); // 'estado_diseno' | 'estado_obra'
  const [statusMenuPos, setStatusMenuPos] = useState(null); // { left, top, openUp }
  const [statusSaving, setStatusSaving] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [allTasksLoading, setAllTasksLoading] = useState(false);
  const [allTasksFilter, setAllTasksFilter] = useState('all');
  const [origenOptions, setOrigenOptions] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // { project }
  const [deletingConfirm, setDeletingConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [equipoMembers, setEquipoMembers] = useState([]);
  const [arquitectaEditId, setArquitectaEditId] = useState(null);
  const [arquitectaAnchor, setArquitectaAnchor] = useState(null); // { left, top, width }
  const [arquitectaSaving, setArquitectaSaving] = useState(false);
  const projectsTableRef = useRef(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  // One editable phase pill (📐 diseño / 🏗️ obra). Both columns share the single
  // portal dropdown, keyed by project id + which column was clicked.
  function renderPhaseCell(p, field, label) {
    const value = (p[field] || '').trim();
    const open = statusEditId === p.id && statusEditField === field;
    return (
      <td style={styles.td} data-status-cell key={field}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (open) closeStatusMenu();
            else openStatusMenu(p, field, e.currentTarget);
          }}
          style={{
            ...styles.statusBadge,
            ...getPhaseStatusStyle(value),
            border: 'none', cursor: 'pointer', padding: '4px 12px',
            fontFamily: "'DM Sans', sans-serif",
            ...(value ? {} : { fontWeight: 500 }),
          }}
          title={`Cambiar estado de ${label}`}
        >
          {value || '—'}
          <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.7 }}>▾</span>
        </button>
        {open && statusMenuPos && createPortal(
          <div
            data-status-menu
            style={{
              ...styles.statusMenu,
              position: 'fixed',
              left: statusMenuPos.left,
              top: statusMenuPos.top,
            }}
            onClick={(e) => e.stopPropagation()}
            role="menu"
          >
            {STATUS_CHOICES.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => changePhaseStatus(p, field, opt)}
                disabled={statusSaving}
                style={{
                  ...styles.statusMenuItem,
                  ...(opt === value ? styles.statusMenuItemActive : {}),
                }}
                role="menuitem"
              >
                <span style={{ ...styles.statusDot, background: phaseDotColor(opt) }} />
                {opt}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </td>
    );
  }

  // Progress is derived from tareas — see src/utils/projectProgress.js.
  function progressBreakdown(project) {
    return getProjectProgressBreakdown(project, taskProgressMap);
  }

  async function openAllTasks() {
    setShowAllTasks(true);
    setAllTasksLoading(true);
    const { data, error } = await supabase
      .from('tareas')
      .select('id, nombre, asignado, categoria, progreso, estado, proyecto_id, tipo, proyectos(nombre)')
      .order('inicio', { nullsFirst: false });
    setAllTasksLoading(false);
    if (error) { alert('Error cargando tareas: ' + error.message); return; }
    setAllTasks(data || []);
  }

  function scrollToProjectsTable() {
    if (projectsTableRef.current) {
      projectsTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function openGanttForTask(task) {
    if (!task?.proyecto_id || !onNavigate) return;
    onNavigate('gantt', {
      projectId: task.proyecto_id,
      ganttType: task.tipo || 'obra',
      taskId: task.id,
    });
    setShowAllTasks(false);
  }

  function classifyTask(task) {
    if (task.progreso >= 100) return 'completed';
    if (!task.progreso || task.progreso === 0) return 'pending';
    return 'in-progress';
  }

  // Saves one of the per-phase estado columns from the table dropdown.
  async function changePhaseStatus(project, field, nextEstado) {
    if (!project || nextEstado === project[field]) { closeStatusMenu(); return; }
    setStatusSaving(true);
    const { data, error } = await supabase
      .from('proyectos')
      .update({ [field]: nextEstado })
      .eq('id', project.id)
      .select();
    setStatusSaving(false);
    closeStatusMenu();
    if (error) {
      alert('Error al actualizar estado: ' + error.message);
      return;
    }
    if (!data || data.length === 0) {
      alert('No se actualizó (posible bloqueo RLS en proyectos)');
      return;
    }
    // Reflect optimistically in the local list so the pill updates instantly.
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, [field]: nextEstado } : p));
    if (detailProject && detailProject.id === project.id) {
      setDetailProject(prev => prev && ({ ...prev, [field]: nextEstado }));
    }
  }

  // Close status dropdown on outside click / Escape. The menu lives in a
  // portal on document.body so the closest('[data-status-cell]') check also
  // accepts clicks tagged with data-status-menu so the menu doesn't snap shut.
  useEffect(() => {
    if (!statusEditId) return;
    function close() {
      setStatusEditId(null);
      setStatusEditField(null);
      setStatusMenuPos(null);
    }
    function onDoc(e) {
      if (e.target.closest('[data-status-cell]')) return;
      if (e.target.closest('[data-status-menu]')) return;
      close();
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    function onScroll() { close(); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [statusEditId]);

  function closeStatusMenu() {
    setStatusEditId(null);
    setStatusEditField(null);
    setStatusMenuPos(null);
  }

  function openStatusMenu(project, field, btnEl) {
    setStatusEditField(field);
    if (!btnEl) { setStatusEditId(project.id); setStatusMenuPos(null); return; }
    const r = btnEl.getBoundingClientRect();
    const MENU_W = 200;
    const MENU_H = 180; // approx; opens up if too close to bottom
    const openUp = (window.innerHeight - r.bottom) < MENU_H + 16;
    const left = Math.min(window.innerWidth - MENU_W - 8, Math.max(8, r.left));
    const top = openUp ? Math.max(8, r.top - MENU_H - 6) : r.bottom + 6;
    setStatusMenuPos({ left, top });
    setStatusEditId(project.id);
  }

  const filteredProjects = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const base = !q ? projects : projects.filter(p =>
      (p.nombre || '').toLowerCase().includes(q) ||
      (p.cliente || '').toLowerCase().includes(q)
    );
    const sorted = [...base].sort((a, b) => {
      const av = a.inicio || '';
      const bv = b.inicio || '';
      if (av === bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortDir === 'desc' ? (av < bv ? 1 : -1) : (av < bv ? -1 : 1);
    });
    return sorted;
  }, [projects, searchQuery, sortDir]);

  function openProject(project) {
    setDetailProject(project);
    setEditingField(null);
    setFieldDraft('');
    setSavedFlash(null);
    autofillClientData(project);
  }

  // On panel open, if this project has empty client fields but the same client
  // exists on another project, copy over the missing data and persist it.
  // `projects` is ordered newest-first, so the first non-empty match wins.
  async function autofillClientData(project) {
    if (!project || !(project.cliente || '').trim()) return;
    const fields = ['cliente_dni', 'cliente_telefono', 'cliente_email', 'cliente_origen'];
    const emptyFields = fields.filter(f => !((project[f] ?? '').toString().trim()));
    if (emptyFields.length === 0) return;
    const nameKey = project.cliente.trim().toLowerCase();
    const others = projects.filter(
      p => p.id !== project.id && (p.cliente || '').trim().toLowerCase() === nameKey
    );
    if (others.length === 0) return;
    const updates = {};
    for (const f of emptyFields) {
      for (const o of others) {
        const v = (o[f] ?? '').toString().trim();
        if (v) { updates[f] = v; break; }
      }
    }
    if (Object.keys(updates).length === 0) return;
    const { error } = await supabase
      .from('proyectos')
      .update(updates)
      .eq('id', project.id);
    if (error) {
      console.warn('[dashboard] client autofill failed:', error.message);
      return;
    }
    setDetailProject(prev => (prev && prev.id === project.id ? { ...prev, ...updates } : prev));
    setProjects(prev => prev.map(p => (p.id === project.id ? { ...p, ...updates } : p)));
    showToast('Datos del cliente completados automáticamente');
  }

  function closeProject() {
    setDetailProject(null);
    setEditingField(null);
    setFieldDraft('');
    setSavedFlash(null);
  }

  function startEditField(field, current) {
    setEditingField(field);
    setFieldDraft(current ?? '');
  }

  function cancelEditField() {
    setEditingField(null);
    setFieldDraft('');
  }

  async function saveField(field, rawValue) {
    if (!detailProject) return;
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    const normalized = value === '' ? null : value;
    const current = detailProject[field] ?? null;
    // Close the editor optimistically so a click that moves straight into
    // another row doesn't get clobbered when the async save settles.
    setEditingField(prev => (prev === field ? null : prev));
    setFieldDraft(prev => (prev === (rawValue ?? '') ? '' : prev));
    if (normalized === current) return;
    setSavingField(field);
    const { error } = await supabase
      .from('proyectos')
      .update({ [field]: normalized })
      .eq('id', detailProject.id);
    setSavingField(null);
    if (error) {
      alert('Error al guardar: ' + error.message);
      return;
    }
    const updated = { ...detailProject, [field]: normalized };
    setDetailProject(updated);
    setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSavedFlash(field);
    setTimeout(() => setSavedFlash(prev => (prev === field ? null : prev)), 1400);
  }

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('proyectos')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setProjects(data);
    setLoading(false);
  }, []);

  const fetchTaskCount = useCallback(async () => {
    const { count } = await supabase
      .from('tareas')
      .select('*', { count: 'exact', head: true });
    if (count !== null) setTaskCount(count);
  }, []);

  const fetchTaskProgress = useCallback(async () => {
    const { data, error } = await supabase
      .from('tareas')
      .select('proyecto_id, progreso, tipo');
    if (error) {
      console.warn('[dashboard] task progress fetch failed:', error.message);
      return;
    }
    // Per-project sums split by tipo so the Dashboard can render diseño /
    // obra breakdowns and a "Ambos" total averaged across the two halves.
    setTaskProgressMap(buildTaskProgressMap(data));
  }, []);

  const fetchOrigenes = useCallback(async () => {
    const { data, error } = await supabase.from('origenes_contacto').select('nombre').order('nombre');
    if (error) {
      console.warn('[dashboard] origenes_contacto fetch failed:', error.message);
      setOrigenOptions([]);
      return;
    }
    setOrigenOptions((data || []).map(o => o.nombre));
  }, []);

  const fetchEquipo = useCallback(async () => {
    const { data, error } = await supabase
      .from('equipo')
      .select('id, nombre, cargo, color, foto_url, estado')
      .order('nombre');
    if (error) {
      console.warn('[dashboard] equipo fetch failed:', error.message);
      setEquipoMembers([]);
      return;
    }
    setEquipoMembers(data || []);
  }, []);

  const equipoById = useMemo(() => {
    const map = {};
    equipoMembers.forEach(m => { map[m.id] = m; });
    return map;
  }, [equipoMembers]);

  // Unique client directory derived from existing projects, used to autocomplete
  // client data in the new-project modal. `projects` is ordered newest-first, so
  // the first occurrence of a name carries that client's most recent data.
  const clientDirectory = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const name = (p.cliente || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (map.has(key)) continue;
      map.set(key, {
        cliente: name,
        proyecto: p.nombre || '',
        cliente_telefono: p.cliente_telefono || '',
        cliente_email: p.cliente_email || '',
        cliente_dni: p.cliente_dni || '',
        cliente_origen: p.cliente_origen || '',
      });
    }
    return Array.from(map.values());
  }, [projects]);

  const clientSuggestions = useMemo(() => {
    const q = (form.cliente || '').trim().toLowerCase();
    if (!q) return [];
    return clientDirectory
      .filter(c => c.cliente.toLowerCase().includes(q) && c.cliente.toLowerCase() !== q)
      .slice(0, 5);
  }, [form.cliente, clientDirectory]);

  function selectClient(c) {
    setForm(prev => ({
      ...prev,
      cliente: c.cliente,
      cliente_telefono: c.cliente_telefono,
      cliente_email: c.cliente_email,
      cliente_dni: c.cliente_dni,
      cliente_origen: c.cliente_origen,
    }));
    setShowClientSuggestions(false);
  }

  useEffect(() => {
    fetchProjects();
    fetchTaskCount();
    fetchTaskProgress();
    fetchOrigenes();
    fetchEquipo();
  }, [fetchProjects, fetchTaskCount, fetchTaskProgress, fetchOrigenes, fetchEquipo]);

  async function changeProjectArquitecta(project, member) {
    if (!project) return;
    const nextId = member ? member.id : null;
    const nextNombre = member ? member.nombre : null;
    if ((project.arquitecta_id || null) === nextId) {
      setArquitectaEditId(null);
      setArquitectaAnchor(null);
      return;
    }
    setArquitectaSaving(true);
    const { data, error } = await supabase
      .from('proyectos')
      .update({ arquitecta_id: nextId, arquitecta_nombre: nextNombre })
      .eq('id', project.id)
      .select();
    setArquitectaSaving(false);
    setArquitectaEditId(null);
    setArquitectaAnchor(null);
    if (error) {
      showToast('Error al asignar arquitecta: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se asignó (posible bloqueo RLS en proyectos)', 'error');
      return;
    }
    setProjects(prev => prev.map(p => p.id === project.id
      ? { ...p, arquitecta_id: nextId, arquitecta_nombre: nextNombre }
      : p
    ));
    if (detailProject && detailProject.id === project.id) {
      setDetailProject(prev => prev && ({ ...prev, arquitecta_id: nextId, arquitecta_nombre: nextNombre }));
    }
    showToast(member ? `Asignada a ${member.nombre}` : 'Arquitecta removida');
  }

  // Close arquitecta dropdown on outside click / Escape, and reposition on scroll/resize.
  useEffect(() => {
    if (!arquitectaEditId) return;
    function onDoc(e) {
      if (e.target.closest('[data-arquitecta-cell]')) return;
      if (e.target.closest('[data-arquitecta-menu]')) return;
      setArquitectaEditId(null);
      setArquitectaAnchor(null);
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setArquitectaEditId(null);
        setArquitectaAnchor(null);
      }
    }
    function reposition() {
      const btn = document.querySelector(`[data-arquitecta-btn="${arquitectaEditId}"]`);
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setArquitectaAnchor({ left: r.left, top: r.top, width: r.width });
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [arquitectaEditId]);

  function requestDeleteProject(project) {
    if (!project) return;
    if (!isAdmin) return; // only admins may open the delete confirmation
    setConfirmDelete({ project });
  }

  function closeConfirmDelete() {
    if (deletingConfirm) return;
    setConfirmDelete(null);
  }

  async function runConfirmDelete() {
    if (!confirmDelete || deletingConfirm) return;
    const project = confirmDelete.project;
    setDeletingConfirm(true);
    // Authorization guard: re-verify against the whitelist at delete time rather
    // than trusting the (potentially stale) client-side isAdmin flag. Confirms the
    // current user is an active admin in the DB before any destructive write.
    if (!currentEmail) {
      setDeletingConfirm(false);
      showToast('No autorizado: sesión sin email', 'error');
      return;
    }
    const { data: wlRow, error: wlErr } = await supabase
      .from('whitelist')
      .select('rol, activo')
      .eq('email', currentEmail)
      .maybeSingle();
    if (wlErr || !wlRow || wlRow.activo === false || wlRow.rol !== 'admin') {
      setDeletingConfirm(false);
      showToast('No autorizado: solo administradores pueden eliminar proyectos', 'error');
      return;
    }
    // journal has no FK → clean it up explicitly (tareas cascade via proyectos FK)
    const { error: journalErr } = await supabase
      .from('journal')
      .delete()
      .eq('proyecto_id', project.id);
    if (journalErr) console.warn('[dashboard] journal delete failed:', journalErr.message);
    const { data, error } = await supabase
      .from('proyectos')
      .delete()
      .eq('id', project.id)
      .select();
    setDeletingConfirm(false);
    if (error) {
      showToast('Error: ' + error.message, 'error');
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se eliminó (posible bloqueo RLS en proyectos)', 'error');
      return;
    }
    setProjects(prev => prev.filter(p => p.id !== project.id));
    setTaskProgressMap(prev => {
      if (!(project.id in prev)) return prev;
      const next = { ...prev };
      delete next[project.id];
      return next;
    });
    if (detailProject && detailProject.id === project.id) {
      setDetailProject(null);
    }
    setConfirmDelete(null);
    showToast('Proyecto eliminado');
  }

  const activeCount = projects.filter(p => p.estado === 'En curso').length;
  const pendingTasks = taskCount;
  const stats = [
    { label: 'Proyectos Activos', value: String(activeCount), change: `${projects.length} total`, icon: '◆', color: '#6B1C2A' },
    { label: 'Tareas Registradas', value: String(pendingTasks), change: 'en Supabase', icon: '◎', color: '#C4922A' },
    { label: 'Recordatorios', value: '7', change: '3 para hoy', icon: '◉', color: '#C9A96E' },
    { label: 'Mensajes Analizados', value: '156', change: '+23 esta semana', icon: '◈', color: '#4A7C59' },
  ];

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const arqId = form.arquitecta_id || null;
    const arqNombre = arqId ? (equipoById[arqId]?.nombre ?? null) : null;
    const nullIfEmpty = (v) => {
      const t = typeof v === 'string' ? v.trim() : v;
      return t === '' ? null : t;
    };
    const payload = {
      ...form,
      arquitecta_id: arqId,
      arquitecta_nombre: arqNombre,
      cliente_telefono: nullIfEmpty(form.cliente_telefono),
      cliente_email: nullIfEmpty(form.cliente_email),
      cliente_dni: nullIfEmpty(form.cliente_dni),
      cliente_origen: nullIfEmpty(form.cliente_origen),
    };
    const { error } = await supabase.from('proyectos').insert([payload]);
    setSaving(false);
    if (!error) {
      setShowModal(false);
      setForm(emptyForm);
      setShowClientSuggestions(false);
      fetchProjects();
    } else {
      alert('Error al guardar: ' + error.message);
    }
  }

  // Phones: the detail panel is a full-screen sheet, so lock the page behind it.
  const detailOpen = !!detailProject;
  useEffect(() => {
    if (!isMobile || !detailOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, detailOpen]);

  // Compact (< 1024px) replacement for the projects table: one card per project.
  // Tapping a card opens the same detail panel a table row opens.
  function renderProjectCards() {
    return (
      <div style={isMobile ? styles.cardList : { ...styles.cardList, ...styles.cardListTablet }}>
        {filteredProjects.map((p) => {
          const isActive = detailProject?.id === p.id;
          const progressBreak = progressBreakdown(p);
          const progressVal = progressBreak.total;
          const showSplit = (p.modalidad || '').trim() === 'Ambos' && (progressBreak.hasDiseno || progressBreak.hasObra);
          const assigned = p.arquitecta_id ? equipoById[p.arquitecta_id] : null;
          const fallbackName = !assigned && p.arquitecta_nombre ? p.arquitecta_nombre : null;
          const display = assigned || (fallbackName ? { nombre: fallbackName } : null);
          const mod = (p.modalidad || '').trim();
          const bars = showSplit
            ? [
                { k: 'd', label: '📐 Diseño', pct: progressBreak.disenoPct ?? 0 },
                { k: 'o', label: '🏗️ Obra', pct: progressBreak.obraPct ?? 0 },
              ]
            : [{ k: 't', label: mod === 'Diseño' ? '📐 Diseño' : mod === 'Obra' ? '🏗️ Obra' : 'Progreso', pct: progressVal }];
          return (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => openProject(p)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProject(p); } }}
              style={{ ...styles.projCard, ...(isActive ? styles.projCardActive : {}) }}
            >
              <div style={styles.projCardTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.projCardName}>{p.nombre}</div>
                  <div style={styles.projCardClient}>{p.cliente || '—'}</div>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); requestDeleteProject(p); }}
                    style={{ ...styles.trashBtn, ...styles.trashBtnTouch }}
                    aria-label={`Eliminar ${p.nombre}`}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>

              <div style={styles.projCardBars}>
                {bars.map(b => (
                  <div key={b.k} style={styles.projCardBarRow}>
                    <span style={styles.projCardBarLabel}>{b.label}</span>
                    <div style={styles.splitProgressBar}>
                      <div style={{ ...styles.progressFill, width: `${b.pct}%`, background: getProgressColor(b.pct) }} />
                    </div>
                    <span style={styles.projCardBarPct}>{b.pct}%</span>
                  </div>
                ))}
                {showSplit && (
                  <div style={styles.projCardTotal}>Total: <strong>{progressVal}%</strong></div>
                )}
              </div>

              <div style={styles.projCardPills}>
                {[
                  { field: 'estado_diseno', label: 'Diseño', icon: '📐' },
                  { field: 'estado_obra', label: 'Obra', icon: '🏗️' },
                ].map(ph => {
                  const value = (p[ph.field] || '').trim();
                  return (
                    <button
                      key={ph.field}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardSheet({ kind: 'estado', projectId: p.id, field: ph.field, label: ph.label });
                      }}
                      style={{ ...styles.statusBadge, ...getPhaseStatusStyle(value), ...styles.projCardPill }}
                      aria-label={`Cambiar estado de ${ph.label}`}
                    >
                      <span aria-hidden="true">{ph.icon}</span>
                      <span>{value || '—'}</span>
                      <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
                    </button>
                  );
                })}
              </div>

              <div style={styles.projCardMeta}>
                <div style={styles.projCardMetaItem}>
                  <span style={styles.projCardMetaLabel}>Arquitecta</span>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardSheet({ kind: 'arquitecta', projectId: p.id });
                      }}
                      style={styles.projCardArqBtn}
                      aria-label="Asignar arquitecta"
                    >
                      {display ? <MiniAvatar member={display} size={22} /> : null}
                      <span style={styles.projCardArqName}>{display ? display.nombre : '— Sin asignar'}</span>
                      <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
                    </button>
                  ) : (
                    <span style={styles.projCardMetaValue}>{display ? display.nombre : '— Sin asignar'}</span>
                  )}
                </div>
                <div style={styles.projCardMetaItem}>
                  <span style={styles.projCardMetaLabel}>Inicio</span>
                  <span style={styles.projCardMetaValue}>{formatDate(p.inicio)}</span>
                </div>
                <div style={styles.projCardMetaItem}>
                  <span style={styles.projCardMetaLabel}>Entrega</span>
                  <span style={styles.projCardMetaValue}>{formatDate(p.fin_estimado)}</span>
                </div>
                <div style={styles.projCardMetaItem}>
                  <span style={styles.projCardMetaLabel}>M²</span>
                  <span style={styles.projCardMetaValue}>
                    {p.metraje != null && p.metraje !== '' ? `${p.metraje} m²` : '—'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const cardSheetProject = cardSheet ? projects.find(x => x.id === cardSheet.projectId) : null;

  return (
    <div style={isMobile ? { ...styles.container, ...pageMobile } : isTablet ? { ...styles.container, ...styles.containerTablet } : styles.container}>
      <header style={isMobile ? { ...styles.header, ...styles.headerMobile } : styles.header}>
        <div>
          <h2 style={isMobile ? { ...styles.title, ...styles.titleMobile } : styles.title}>Dashboard</h2>
          <p style={isMobile ? { ...styles.subtitle, ...styles.textMobile } : styles.subtitle}>Resumen general del estudio</p>
        </div>
        <div style={isMobile ? { ...styles.headerRight, textAlign: 'left' } : styles.headerRight}>
          <span style={isMobile ? { ...styles.date, ...styles.textMobile } : styles.date}>
            {new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </header>

      <div style={isMobile ? { ...styles.statsGrid, ...kpiGridMobile, marginBottom: 20 } : isTablet ? { ...styles.statsGrid, ...styles.statsGridTablet } : styles.statsGrid}>
        {stats.map((stat, i) => {
          const handleClick = (() => {
            switch (stat.label) {
              case 'Proyectos Activos':   return scrollToProjectsTable;
              case 'Tareas Registradas':  return openAllTasks;
              case 'Recordatorios':       return () => onNavigate && onNavigate('whatsapp');
              case 'Mensajes Analizados': return () => onNavigate && onNavigate('whatsapp');
              default: return null;
            }
          })();
          return (
            <div
              key={i}
              style={isMobile ? { ...styles.statCard, cursor: handleClick ? 'pointer' : 'default', ...styles.statCardMobile } : { ...styles.statCard, cursor: handleClick ? 'pointer' : 'default' }}
              onClick={handleClick || undefined}
              onMouseEnter={isTouch && isCompact ? undefined : e => {
                if (!handleClick) return;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 24px rgba(107,28,42,0.08)';
              }}
              onMouseLeave={e => {
                if (!handleClick) return;
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
              role={handleClick ? 'button' : undefined}
              tabIndex={handleClick ? 0 : undefined}
              onKeyDown={handleClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } } : undefined}
            >
              <div style={styles.statTop}>
                <span style={{ ...styles.statIcon, color: stat.color }}>{stat.icon}</span>
                <span style={styles.statChange}>{stat.change}</span>
              </div>
              <div style={isMobile ? { ...styles.statValue, ...styles.statValueMobile } : styles.statValue}>{stat.value}</div>
              <div style={isMobile ? { ...styles.statLabel, ...styles.statLabelMobile } : styles.statLabel}>{stat.label}</div>
            </div>
          );
        })}
      </div>

      <div ref={projectsTableRef} style={styles.tableCard}>
        <div style={isCompact ? { ...styles.tableHeader, ...styles.tableHeaderCompact } : styles.tableHeader}>
          <h3 style={styles.tableTitle}>Proyectos</h3>
          <div style={isCompact ? { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' } : { display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={styles.tableBadge}>
              {searchQuery.trim()
                ? `${filteredProjects.length} de ${projects.length} proyectos`
                : `${projects.length} proyectos`}
            </span>
            {!isMobile && (
            <button
              type="button"
              onClick={() => setSortDir(prev => prev === 'desc' ? 'asc' : 'desc')}
              style={isTablet ? { ...styles.sortToggleBtn, ...styles.tapMin } : styles.sortToggleBtn}
              title="Ordenar por fecha de inicio"
            >
              {sortDir === 'desc' ? '↓ Más reciente' : '↑ Más antiguo'}
            </button>
            )}
            <button style={isCompact ? { ...styles.addBtn, ...styles.tapMin } : styles.addBtn} onClick={() => setShowModal(true)}>
              + Nuevo Proyecto
            </button>
          </div>
        </div>

        <div style={isMobile ? { ...styles.searchRow, ...styles.searchRowMobile } : isTablet ? { ...styles.searchRow, padding: '0 16px 14px' } : styles.searchRow}>
          <div style={isMobile ? { ...styles.searchWrap, ...styles.searchWrapMobile } : styles.searchWrap}>
            <span style={styles.searchIcon} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="9" r="6" />
                <line x1="13.5" y1="13.5" x2="17" y2="17" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por proyecto o cliente..."
              style={isCompact ? { ...styles.searchInput, minHeight: TAP_TARGET } : styles.searchInput}
              aria-label="Buscar proyectos"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={isCompact ? { ...styles.searchClearBtn, ...styles.searchClearBtnTouch } : styles.searchClearBtn}
                aria-label="Limpiar búsqueda"
                title="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>
          {isMobile && (
            <button type="button" style={styles.filtersBtn} onClick={() => setFiltersOpen(true)}>
              Filtros
            </button>
          )}
        </div>

        <div style={styles.tableWrap}>
          {loading ? (
            <div style={styles.loadingMsg}>Cargando proyectos...</div>
          ) : projects.length === 0 ? (
            <div style={styles.loadingMsg}>No hay proyectos. Crea el primero.</div>
          ) : filteredProjects.length === 0 ? (
            <div style={styles.loadingMsg}>No se encontraron proyectos para tu búsqueda</div>
          ) : isCompact ? (
            renderProjectCards()
          ) : (
            <table style={{ ...styles.table, tableLayout: 'fixed', minWidth: 1120 }}>
              {/* Percentages so the table breathes on wide screens; minWidth
                  above keeps it readable (and scrollable) on narrow ones. */}
              <colgroup>
                <col style={{ width: '16%' }} />{/* Proyecto */}
                <col style={{ width: '13%' }} />{/* Cliente */}
                <col style={{ width: '11%' }} />{/* Arquitecta */}
                <col style={{ width: '8%' }} />{/* Inicio */}
                <col style={{ width: '8%' }} />{/* Entrega */}
                <col style={{ width: '17%' }} />{/* Progreso */}
                <col style={{ width: '6%' }} />{/* M² */}
                <col style={{ width: '9%' }} />{/* Diseño */}
                <col style={{ width: '9%' }} />{/* Obra */}
                <col style={{ width: '3%' }} />{/* Acciones */}
              </colgroup>
              <thead>
                <tr>
                  <th style={styles.th}>Proyecto</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Arquitecta</th>
                  <th style={styles.th}>Inicio</th>
                  <th style={styles.th}>Entrega</th>
                  <th style={styles.th}>Progreso</th>
                  <th style={styles.th}>M²</th>
                  <th style={styles.th}>📐 Diseño</th>
                  <th style={styles.th}>🏗️ Obra</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((p) => {
                  const isActive = detailProject?.id === p.id;
                  const progressBreak = progressBreakdown(p);
                  const progressVal = progressBreak.total;
                  const showSplit = (p.modalidad || '').trim() === 'Ambos' && (progressBreak.hasDiseno || progressBreak.hasObra);
                  return (
                    <tr
                      key={p.id}
                      style={{ ...styles.tr, ...(isActive ? styles.trActive : styles.trClickable) }}
                      onClick={() => openProject(p)}
                    >
                      <td style={styles.td}>
                        <span style={styles.projectName} title={p.nombre}>{p.nombre}</span>
                      </td>
                      <td style={{ ...styles.td, color: 'var(--text-secondary)' }} title={p.cliente || ''}>
                        <span style={styles.cellTruncate}>{p.cliente}</span>
                      </td>
                      <td style={{ ...styles.td, position: 'relative', overflow: 'visible' }} data-arquitecta-cell>
                        {(() => {
                          const assigned = p.arquitecta_id ? equipoById[p.arquitecta_id] : null;
                          const fallbackName = !assigned && p.arquitecta_nombre ? p.arquitecta_nombre : null;
                          const display = assigned || (fallbackName ? { nombre: fallbackName } : null);
                          const isOpen = arquitectaEditId === p.id;
                          return (
                            <>
                              <button
                                type="button"
                                data-arquitecta-btn={p.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isAdmin) return;
                                  if (isOpen) {
                                    setArquitectaEditId(null);
                                    setArquitectaAnchor(null);
                                    return;
                                  }
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setArquitectaAnchor({ left: r.left, top: r.top, width: r.width });
                                  setArquitectaEditId(p.id);
                                }}
                                disabled={!isAdmin}
                                style={{
                                  ...styles.arquitectaPill,
                                  cursor: isAdmin ? 'pointer' : 'default',
                                  opacity: isAdmin ? 1 : 0.95,
                                }}
                                title={isAdmin ? 'Asignar arquitecta' : (display?.nombre || 'Sin asignar')}
                              >
                                {display ? (
                                  <>
                                    <MiniAvatar member={display} size={22} />
                                    <span style={styles.arquitectaName}>{display.nombre}</span>
                                  </>
                                ) : (
                                  <span style={styles.arquitectaEmpty}>— Sin asignar</span>
                                )}
                                {isAdmin && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.6 }}>▾</span>}
                              </button>
                              {isOpen && isAdmin && arquitectaAnchor && createPortal(
                                <div
                                  data-arquitecta-menu
                                  style={{
                                    ...styles.arquitectaMenu,
                                    left: arquitectaAnchor.left,
                                    bottom: window.innerHeight - arquitectaAnchor.top + 4,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  role="menu"
                                >
                                  <button
                                    type="button"
                                    onClick={() => changeProjectArquitecta(p, null)}
                                    disabled={arquitectaSaving}
                                    style={{
                                      ...styles.arquitectaMenuItem,
                                      ...((!p.arquitecta_id) ? styles.arquitectaMenuItemActive : {}),
                                    }}
                                    role="menuitem"
                                  >
                                    <span style={{ fontSize: 12, color: 'var(--text-light)' }}>— Sin asignar</span>
                                  </button>
                                  {equipoMembers.map(m => {
                                    const active = p.arquitecta_id === m.id;
                                    return (
                                      <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => changeProjectArquitecta(p, m)}
                                        disabled={arquitectaSaving}
                                        style={{
                                          ...styles.arquitectaMenuItem,
                                          ...(active ? styles.arquitectaMenuItemActive : {}),
                                        }}
                                        role="menuitem"
                                      >
                                        <MiniAvatar member={m} size={22} />
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {m.nombre}
                                        </span>
                                        {m.cargo && <span style={styles.arquitectaMenuCargo}>{m.cargo}</span>}
                                      </button>
                                    );
                                  })}
                                </div>,
                                document.body
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ ...styles.td, color: 'var(--text-secondary)' }}>{formatDate(p.inicio)}</td>
                      <td style={{ ...styles.td, color: 'var(--text-secondary)' }}>{formatDate(p.fin_estimado)}</td>
                      <td style={styles.td}>
                        {showSplit ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <div style={styles.splitProgressRow}>
                              <span style={styles.splitProgressLabel}>📐 Diseño</span>
                              <div style={styles.splitProgressBar}>
                                <div style={{
                                  ...styles.progressFill,
                                  width: `${progressBreak.disenoPct ?? 0}%`,
                                  background: getProgressColor(progressBreak.disenoPct ?? 0),
                                }} />
                              </div>
                              <span style={styles.splitProgressPct}>{progressBreak.disenoPct ?? 0}%</span>
                            </div>
                            <div style={styles.splitProgressRow}>
                              <span style={styles.splitProgressLabel}>🏗️ Obra</span>
                              <div style={styles.splitProgressBar}>
                                <div style={{
                                  ...styles.progressFill,
                                  width: `${progressBreak.obraPct ?? 0}%`,
                                  background: getProgressColor(progressBreak.obraPct ?? 0),
                                }} />
                              </div>
                              <span style={styles.splitProgressPct}>{progressBreak.obraPct ?? 0}%</span>
                            </div>
                            <div style={styles.splitProgressTotal}>
                              Total: <strong>{progressVal}%</strong>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {((p.modalidad || '').trim() === 'Diseño' || (p.modalidad || '').trim() === 'Obra') && (
                              <span style={styles.modalidadLabel}>
                                {(p.modalidad || '').trim() === 'Diseño' ? '📐 Diseño' : '🏗️ Obra'}
                              </span>
                            )}
                            <div style={styles.progressCell}>
                              <div style={styles.progressBar}>
                                <div style={{
                                  ...styles.progressFill,
                                  width: `${progressVal}%`,
                                  background: getProgressColor(progressVal),
                                }} />
                              </div>
                              <span style={styles.progressText}>{progressVal}%</span>
                            </div>
                          </div>
                        )}
                        <PhaseBadges diseno={p.estado_diseno} obra={p.estado_obra} />
                      </td>
                      <td style={{ ...styles.td, color: 'var(--text-secondary)' }}>
                        {p.metraje != null && p.metraje !== '' ? `${p.metraje} m²` : '—'}
                      </td>
                      {renderPhaseCell(p, 'estado_diseno', 'Diseño')}
                      {renderPhaseCell(p, 'estado_obra', 'Obra')}
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); requestDeleteProject(p); }}
                            style={styles.trashBtn}
                            title="Eliminar proyecto"
                            aria-label={`Eliminar ${p.nombre}`}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Client side panel */}
      {detailProject && (
        <>
          <div style={styles.panelBackdrop} onClick={closeProject} />
          <aside style={isMobile ? { ...styles.sidePanel, ...styles.sidePanelMobile } : styles.sidePanel} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...styles.panelHeader, ...styles.panelHeaderMobile } : styles.panelHeader}>
              <div style={{ minWidth: 0 }}>
                <div style={styles.panelEyebrow}>Cliente · Vista previa</div>
                <h3 style={styles.panelTitle}>{detailProject.cliente || 'Cliente sin nombre'}</h3>
                <div style={{ marginTop: 6 }}>
                  <span style={{ ...styles.statusBadge, ...getStatusStyle(detailProject.estado) }}>
                    {detailProject.estado}
                  </span>
                </div>
              </div>
              <button style={isMobile ? { ...styles.closeBtn, ...sheet.closeBtn, flexShrink: 0 } : styles.closeBtn} onClick={closeProject} aria-label="Cerrar">✕</button>
            </div>

            <div style={isMobile ? { ...styles.panelBody, ...styles.panelBodyMobile } : styles.panelBody}>
              <section>
                <div style={styles.sectionLabel}>Datos del cliente</div>

                <div style={styles.detailList}>
                  <InlineRow
                    label="Nombre"
                    field="cliente"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.cliente}
                    onStartEdit={() => startEditField('cliente', detailProject.cliente)}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="text"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('cliente', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="DNI"
                    field="cliente_dni"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.cliente_dni}
                    mono
                    onStartEdit={() => startEditField('cliente_dni', detailProject.cliente_dni)}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="text"
                        placeholder="Ej. 12345678"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('cliente_dni', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Origen de contacto"
                    field="cliente_origen"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.cliente_origen}
                    onStartEdit={() => startEditField('cliente_origen', detailProject.cliente_origen)}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={origenOptions}
                        onCommit={(v) => saveField('cliente_origen', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Teléfono"
                    field="cliente_telefono"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={formatPhoneDisplay(detailProject.cliente_telefono)}
                    mono
                    onStartEdit={() => startEditField('cliente_telefono', detailProject.cliente_telefono)}
                    editor={(
                      <InlinePhoneEditor
                        value={fieldDraft}
                        onChange={setFieldDraft}
                        onCommit={() => saveField('cliente_telefono', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Email"
                    field="cliente_email"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.cliente_email}
                    onStartEdit={() => startEditField('cliente_email', detailProject.cliente_email)}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="email"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('cliente_email', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Metraje (m²)"
                    field="metraje"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.metraje != null && detailProject.metraje !== '' ? `${detailProject.metraje} m²` : ''}
                    mono
                    onStartEdit={() => startEditField('metraje', detailProject.metraje ?? '')}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="number"
                        placeholder="Ej. 120"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('metraje', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                </div>
              </section>

              <div style={styles.divider} />

              <section>
                <div style={styles.sectionLabel}>Proyecto asociado</div>
                <div style={styles.projectCard}>
                  {editingField === 'nombre' ? (
                    <input
                      autoFocus
                      value={fieldDraft}
                      onChange={(e) => setFieldDraft(e.target.value)}
                      onBlur={() => saveField('nombre', fieldDraft)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancelEditField(); }
                      }}
                      style={{ ...styles.projectCardName, ...styles.projectCardNameInput }}
                    />
                  ) : (
                    <div
                      style={{ ...styles.projectCardName, ...styles.projectCardNameEditable }}
                      title="Click para editar"
                      onClick={() => startEditField('nombre', detailProject.nombre || '')}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(107,28,42,0.05)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                        {detailProject.nombre || <span style={styles.detailEmpty}>Sin nombre</span>}
                      </span>
                      {savingField === 'nombre' && <span style={styles.inlineStatus}>…</span>}
                      {savingField !== 'nombre' && savedFlash === 'nombre' && (
                        <span style={{ ...styles.inlineStatus, color: '#4A7C59' }}>✓</span>
                      )}
                    </div>
                  )}
                  <div style={styles.projectCardMeta}>
                    {(detailProject.tipo || '—')}
                  </div>
                  {(() => {
                    const mod = (detailProject.modalidad || '').trim();
                    if (!mod) return null;
                    const label =
                      mod === 'Diseño' ? '📐 Solo Diseño' :
                      mod === 'Obra'   ? '🏗️ Solo Obra' :
                      mod === 'Ambos'  ? '📐🏗️ Diseño + Obra' : mod;
                    return <span style={styles.modalidadBadge}>{label}</span>;
                  })()}
                  <div style={styles.projectDates}>
                    <div>
                      <span style={styles.projectDateLabel}>Inicio</span>
                      <span style={styles.projectDateValue}>{formatDate(detailProject.inicio)}</span>
                    </div>
                    <div>
                      <span style={styles.projectDateLabel}>Entrega</span>
                      <span style={styles.projectDateValue}>{formatDate(detailProject.fin_estimado)}</span>
                    </div>
                  </div>
                  {(() => {
                    const brk = progressBreakdown(detailProject);
                    const showSplitDetail = (detailProject.modalidad || '').trim() === 'Ambos' && (brk.hasDiseno || brk.hasObra);
                    if (showSplitDetail) {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                          <div style={styles.splitProgressRow}>
                            <span style={{ ...styles.splitProgressLabel, fontSize: 11.5 }}>📐 Diseño</span>
                            <div style={{ ...styles.splitProgressBar, height: 6 }}>
                              <div style={{
                                ...styles.progressFill,
                                width: `${brk.disenoPct ?? 0}%`,
                                background: getProgressColor(brk.disenoPct ?? 0),
                              }} />
                            </div>
                            <span style={styles.splitProgressPct}>{brk.disenoPct ?? 0}%</span>
                          </div>
                          <div style={styles.splitProgressRow}>
                            <span style={{ ...styles.splitProgressLabel, fontSize: 11.5 }}>🏗️ Obra</span>
                            <div style={{ ...styles.splitProgressBar, height: 6 }}>
                              <div style={{
                                ...styles.progressFill,
                                width: `${brk.obraPct ?? 0}%`,
                                background: getProgressColor(brk.obraPct ?? 0),
                              }} />
                            </div>
                            <span style={styles.splitProgressPct}>{brk.obraPct ?? 0}%</span>
                          </div>
                          <div style={{ ...styles.splitProgressTotal, fontSize: 12 }}>
                            Total: <strong>{brk.total}%</strong>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                        <div style={{ ...styles.progressBar, height: 8, flex: 1 }}>
                          <div style={{
                            ...styles.progressFill,
                            width: `${brk.total}%`,
                            background: getProgressColor(brk.total),
                          }} />
                        </div>
                        <span style={{ ...styles.progressText, fontSize: 13 }}>{brk.total}%</span>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ ...styles.detailList, marginTop: 12 }}>
                  <InlineRow
                    label="Tipo de proyecto"
                    field="tipo"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.tipo}
                    onStartEdit={() => startEditField('tipo', detailProject.tipo || '')}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={['Residencial', 'Hospitalario', 'Corporativo', 'Comercial', 'Oficinas', 'Otro']}
                        onCommit={(v) => saveField('tipo', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Modalidad"
                    field="modalidad"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.modalidad}
                    onStartEdit={() => startEditField('modalidad', detailProject.modalidad || '')}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={['Diseño', 'Obra', 'Ambos']}
                        onCommit={(v) => saveField('modalidad', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Estado general"
                    field="estado"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.estado}
                    onStartEdit={() => startEditField('estado', detailProject.estado || '')}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={STATUS_CHOICES}
                        onCommit={(v) => saveField('estado', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Estado de Diseño"
                    field="estado_diseno"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.estado_diseno}
                    onStartEdit={() => startEditField('estado_diseno', detailProject.estado_diseno || '')}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={STATUS_CHOICES}
                        onCommit={(v) => saveField('estado_diseno', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Estado de Obra"
                    field="estado_obra"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={detailProject.estado_obra}
                    onStartEdit={() => startEditField('estado_obra', detailProject.estado_obra || '')}
                    editor={(
                      <InlineSelectEditor
                        value={fieldDraft}
                        options={STATUS_CHOICES}
                        onCommit={(v) => saveField('estado_obra', v)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Fecha de inicio"
                    field="inicio"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={formatDateDMY(detailProject.inicio)}
                    mono
                    onStartEdit={() => startEditField('inicio', detailProject.inicio || '')}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="date"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('inicio', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                  <InlineRow
                    label="Fecha de entrega estimada"
                    field="fin_estimado"
                    editingField={editingField}
                    savingField={savingField}
                    savedFlash={savedFlash}
                    displayValue={formatDateDMY(detailProject.fin_estimado)}
                    mono
                    onStartEdit={() => startEditField('fin_estimado', detailProject.fin_estimado || '')}
                    editor={(
                      <InlineTextEditor
                        value={fieldDraft}
                        type="date"
                        onChange={setFieldDraft}
                        onCommit={() => saveField('fin_estimado', fieldDraft)}
                        onCancel={cancelEditField}
                      />
                    )}
                  />
                </div>
              </section>
            </div>

            <div style={isMobile ? { ...styles.panelActions, ...styles.panelActionsMobile } : styles.panelActions}>
              <div style={styles.ganttBtnRow}>
                <button
                  style={isMobile ? { ...styles.ganttDesignBtn, ...styles.btnMobile } : styles.ganttDesignBtn}
                  onClick={() => {
                    const id = detailProject.id;
                    closeProject();
                    onNavigate && onNavigate('gantt', { projectId: id, ganttType: 'diseño' });
                  }}
                >
                  <span style={styles.ganttEmoji} aria-hidden="true">📐</span>
                  <span>Gantt Diseño</span>
                </button>
                <button
                  style={isMobile ? { ...styles.ganttBuildBtn, ...styles.btnMobile } : styles.ganttBuildBtn}
                  onClick={() => {
                    const id = detailProject.id;
                    closeProject();
                    onNavigate && onNavigate('gantt', { projectId: id, ganttType: 'obra' });
                  }}
                >
                  <span style={styles.ganttEmoji} aria-hidden="true">🏗️</span>
                  <span>Gantt Obra</span>
                </button>
              </div>
              <button
                style={isMobile ? { ...styles.secondaryBtn, ...styles.btnMobile } : styles.secondaryBtn}
                onClick={() => {
                  closeProject();
                  onNavigate && onNavigate('whatsapp');
                }}
              >
                Ver WhatsApp
              </button>
            </div>
          </aside>
        </>
      )}

      {/* All Tasks Modal (triggered by Tareas Registradas stat card) */}
      {showAllTasks && (
        <div style={isMobile ? { ...styles.overlay, ...sheet.overlay } : styles.overlay} onClick={() => setShowAllTasks(false)}>
          <div style={isMobile ? { ...styles.modal, maxWidth: 820, ...sheet.panel } : isTablet ? { ...styles.modal, maxWidth: 820, ...styles.modalTablet } : { ...styles.modal, maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
            <div style={isMobile ? { ...styles.modalHeader, ...styles.modalHeaderMobile, ...sheet.header } : styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Todas las tareas</h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                  Clic en una tarea para abrir su Gantt.
                </p>
              </div>
              <button style={isMobile ? { ...styles.closeBtn, ...sheet.closeBtn, flexShrink: 0 } : styles.closeBtn} onClick={() => setShowAllTasks(false)}>✕</button>
            </div>

            <div style={isMobile ? { ...styles.allTasksFilters, ...styles.allTasksFiltersMobile } : styles.allTasksFilters}>
              {[
                { v: 'all',         l: 'Todas' },
                { v: 'pending',     l: 'Pendientes' },
                { v: 'in-progress', l: 'En progreso' },
                { v: 'completed',   l: 'Completadas' },
              ].map(f => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => setAllTasksFilter(f.v)}
                  style={{
                    ...styles.allTasksFilterBtn,
                    ...(allTasksFilter === f.v ? styles.allTasksFilterBtnActive : {}),
                  }}
                >
                  {f.l}
                </button>
              ))}
            </div>

            <div style={isMobile ? { padding: '0 16px 24px' } : { padding: '0 28px 24px' }}>
              {allTasksLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-light)' }}>Cargando…</div>
              ) : (() => {
                const filtered = allTasks.filter(t => allTasksFilter === 'all' || classifyTask(t) === allTasksFilter);
                if (filtered.length === 0) {
                  return <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-light)' }}>No hay tareas que coincidan con este filtro.</div>;
                }
                return (
                  <div style={isMobile ? { ...styles.allTasksList, ...styles.allTasksListMobile } : styles.allTasksList}>
                    {filtered.map(t => {
                      const kind = classifyTask(t);
                      const projectName = t.proyectos?.nombre || '—';
                      return (
                        <div
                          key={t.id}
                          onClick={() => openGanttForTask(t)}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(107,28,42,0.35)'; e.currentTarget.style.background = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = '#FDFBF8'; }}
                          style={styles.allTasksRow}
                          role="button"
                          tabIndex={0}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={styles.allTasksName}>{t.nombre || '(sin nombre)'}</div>
                            <div style={styles.allTasksMeta}>
                              {projectName} · {t.categoria || '—'} · {t.asignado || 'Sin asignar'}
                            </div>
                          </div>
                          <span style={{ ...styles.allTasksPill, ...(kind === 'completed' ? styles.allTasksPillDone : kind === 'in-progress' ? styles.allTasksPillProg : styles.allTasksPillPend) }}>
                            {t.progreso ?? 0}%
                          </span>
                          <span style={{ color: 'var(--wine)', fontSize: 14 }} aria-hidden="true">→</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showModal && (
        <div style={isMobile ? { ...styles.overlay, ...sheet.overlay } : styles.overlay} onClick={() => setShowModal(false)}>
          <div style={isMobile ? { ...styles.modal, ...sheet.panel } : styles.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...styles.modalHeader, ...styles.modalHeaderMobile, ...sheet.header } : styles.modalHeader}>
              <h3 style={styles.modalTitle}>Nuevo Proyecto</h3>
              <button style={isMobile ? { ...styles.closeBtn, ...sheet.closeBtn, flexShrink: 0 } : styles.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={isMobile ? { ...styles.form, ...styles.formMobile } : styles.form}>
              <div style={isMobile ? { ...styles.fieldRow, ...styles.fieldRowMobile } : styles.fieldRow}>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Nombre del proyecto</span>
                  <input style={styles.input} name="nombre" value={form.nombre} onChange={handleChange} required placeholder="Ej. Residencia Las Lomas" />
                </label>
                <label style={{ ...styles.fieldGroup, position: 'relative' }}>
                  <span style={styles.fieldLabel}>Cliente</span>
                  <input
                    style={styles.input}
                    name="cliente"
                    value={form.cliente}
                    onChange={(e) => { handleChange(e); setShowClientSuggestions(true); }}
                    onFocus={() => setShowClientSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowClientSuggestions(false), 120)}
                    required
                    placeholder="Ej. Familia García"
                    autoComplete="off"
                  />
                  {showClientSuggestions && clientSuggestions.length > 0 && (
                    <div style={styles.clientSuggestions}>
                      {clientSuggestions.map((c) => (
                        <button
                          key={c.cliente}
                          type="button"
                          style={styles.clientSuggestionItem}
                          onMouseDown={(e) => { e.preventDefault(); selectClient(c); }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(107,28,42,0.06)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <span style={styles.clientSuggestionName}>{c.cliente}</span>
                          {c.proyecto && (
                            <span style={styles.clientSuggestionMeta}>{c.proyecto}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </label>
              </div>
              <div style={isMobile ? { ...styles.fieldRow, ...styles.fieldRowMobile } : styles.fieldRow}>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Tipo</span>
                  <select style={styles.input} name="tipo" value={form.tipo} onChange={handleChange}>
                    <option>Residencial</option>
                    <option>Hospitalario</option>
                    <option>Corporativo</option>
                    <option>Comercial</option>
                    <option>Oficinas</option>
                    <option>Otro</option>
                  </select>
                </label>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Modalidad</span>
                  <select style={styles.input} name="modalidad" value={form.modalidad} onChange={handleChange}>
                    <option>Diseño</option>
                    <option>Obra</option>
                    <option>Ambos</option>
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...styles.fieldRow, ...styles.fieldRowMobile } : styles.fieldRow}>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Estado</span>
                  <select style={styles.input} name="estado" value={form.estado} onChange={handleChange}>
                    <option>En curso</option>
                    <option>Pendiente</option>
                    <option>Pausado</option>
                    <option>Completado</option>
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...styles.fieldRow, ...styles.fieldRowMobile } : styles.fieldRow}>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Fecha de inicio</span>
                  <input style={styles.input} type="date" name="inicio" value={form.inicio} onChange={handleChange} required />
                </label>
                <label style={styles.fieldGroup}>
                  <span style={styles.fieldLabel}>Fecha estimada de fin</span>
                  <input style={styles.input} type="date" name="fin_estimado" value={form.fin_estimado} onChange={handleChange} required />
                </label>
              </div>
              <label style={styles.fieldGroup}>
                <span style={styles.fieldLabel}>Arquitecta responsable</span>
                <select style={styles.input} name="arquitecta_id" value={form.arquitecta_id || ''} onChange={handleChange}>
                  <option value="">— Sin asignar</option>
                  {equipoMembers.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.nombre}{m.cargo ? ` · ${m.cargo}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div style={isMobile ? { ...styles.modalActions, ...styles.modalActionsMobile, ...sheet.footer } : styles.modalActions}>
                <button type="button" style={isMobile ? { ...styles.cancelBtn, ...styles.btnMobile } : styles.cancelBtn} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...styles.submitBtn, ...styles.btnMobile } : styles.submitBtn} disabled={saving}>
                  {saving ? 'Guardando...' : 'Crear Proyecto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={isMobile ? { ...styles.overlay, ...dialog.overlay } : styles.overlay} onClick={closeConfirmDelete}>
          <div style={isMobile ? { ...styles.modal, maxWidth: 460, ...dialog.panel } : { ...styles.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={isMobile ? { ...styles.modalHeader, ...styles.modalHeaderMobile, borderBottom: 'none', paddingRight: 8 } : styles.modalHeader}>
              <h3 style={styles.modalTitle}>Eliminar proyecto</h3>
              <button style={isMobile ? { ...styles.closeBtn, ...sheet.closeBtn, flexShrink: 0 } : styles.closeBtn} onClick={closeConfirmDelete} disabled={deletingConfirm}>✕</button>
            </div>
            <div style={isMobile ? { padding: '4px 16px 16px' } : { padding: '14px 28px 24px' }}>
              <p style={isMobile ? { fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 18px' } : { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 20px' }}>
                ¿Eliminar proyecto {confirmDelete.project.nombre}? Se eliminarán también todas sus tareas y notas.
              </p>
              <div style={isMobile ? { ...styles.modalActions, flexWrap: 'wrap' } : styles.modalActions}>
                <button type="button" style={isMobile ? { ...styles.cancelBtn, ...styles.btnMobile } : styles.cancelBtn} onClick={closeConfirmDelete} disabled={deletingConfirm}>
                  Cancelar
                </button>
                <button type="button" style={isMobile ? { ...styles.dangerBtn, ...styles.btnMobile } : styles.dangerBtn} onClick={runConfirmDelete} disabled={deletingConfirm}>
                  {deletingConfirm ? 'Eliminando…' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <BottomSheet
          open={filtersOpen}
          onClose={closeFilters}
          title="Filtros"
          footer={(
            <button type="button" style={{ ...styles.submitBtn, ...styles.tapMin, flex: 1 }} onClick={closeFilters}>
              Ver {filteredProjects.length} proyectos
            </button>
          )}
        >
          <div style={styles.sheetSectionLabel}>Ordenar por fecha de inicio</div>
          {[
            { v: 'desc', l: '↓ Más reciente primero' },
            { v: 'asc', l: '↑ Más antiguo primero' },
          ].map(o => (
            <button
              key={o.v}
              type="button"
              onClick={() => setSortDir(o.v)}
              style={{ ...styles.sheetOption, ...(sortDir === o.v ? styles.sheetOptionActive : {}) }}
            >
              <span style={{ flex: 1 }}>{o.l}</span>
              {sortDir === o.v && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </BottomSheet>
      )}

      {isCompact && (
        <BottomSheet
          open={!!cardSheet && !!cardSheetProject}
          onClose={closeCardSheet}
          title={cardSheet?.kind === 'arquitecta' ? 'Asignar arquitecta' : `Estado de ${cardSheet?.label || ''}`}
        >
          {cardSheetProject && cardSheet?.kind === 'estado' && STATUS_CHOICES.map(opt => {
            const active = (cardSheetProject[cardSheet.field] || '').trim() === opt;
            return (
              <button
                key={opt}
                type="button"
                disabled={statusSaving}
                onClick={async () => {
                  await changePhaseStatus(cardSheetProject, cardSheet.field, opt);
                  setCardSheet(null);
                }}
                style={{ ...styles.sheetOption, ...(active ? styles.sheetOptionActive : {}) }}
              >
                <span style={{ ...styles.statusDot, background: phaseDotColor(opt) }} />
                <span style={{ flex: 1 }}>{opt}</span>
                {active && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
          {cardSheetProject && cardSheet?.kind === 'arquitecta' && isAdmin && (
            <>
              <button
                type="button"
                disabled={arquitectaSaving}
                onClick={async () => {
                  await changeProjectArquitecta(cardSheetProject, null);
                  setCardSheet(null);
                }}
                style={{ ...styles.sheetOption, ...(!cardSheetProject.arquitecta_id ? styles.sheetOptionActive : {}) }}
              >
                <span style={{ flex: 1, color: 'var(--text-light)' }}>— Sin asignar</span>
              </button>
              {equipoMembers.map(m => {
                const active = cardSheetProject.arquitecta_id === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={arquitectaSaving}
                    onClick={async () => {
                      await changeProjectArquitecta(cardSheetProject, m);
                      setCardSheet(null);
                    }}
                    style={{ ...styles.sheetOption, ...(active ? styles.sheetOptionActive : {}) }}
                  >
                    <MiniAvatar member={m} size={26} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.nombre}</span>
                    {m.cargo && <span style={styles.arquitectaMenuCargo}>{m.cargo}</span>}
                  </button>
                );
              })}
            </>
          )}
        </BottomSheet>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color: '#fff', padding: '10px 24px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          fontFamily: "'DM Sans',sans-serif", zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? toastMobile : {}),
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

function InlineRow({ label, field, editingField, savingField, savedFlash, displayValue, mono, onStartEdit, editor }) {
  const { isMobile } = useBreakpoint();
  const isEditing = editingField === field;
  const isSaving = savingField === field;
  const justSaved = savedFlash === field;
  return (
    <div style={isMobile ? { ...styles.detailLine, ...styles.detailLineMobile } : styles.detailLine}>
      <span style={styles.detailLineLabel}>{label}</span>
      {isEditing ? (
        <div style={styles.inlineEditorWrap}>{editor}</div>
      ) : (
        <button
          type="button"
          onClick={onStartEdit}
          style={{
            ...styles.inlineDisplay,
            ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } : {}),
            ...(isMobile ? styles.inlineDisplayMobile : {}),
          }}
          title="Click para editar"
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(107,28,42,0.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
            {displayValue || <span style={styles.detailEmpty}>—</span>}
          </span>
          {isSaving && <span style={styles.inlineStatus}>…</span>}
          {!isSaving && justSaved && <span style={{ ...styles.inlineStatus, color: '#4A7C59' }}>✓</span>}
        </button>
      )}
    </div>
  );
}

function InlineTextEditor({ value, onChange, onCommit, onCancel, type = 'text', placeholder }) {
  return (
    <input
      autoFocus
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      onBlur={() => onCommit()}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      }}
      style={styles.inlineInput}
    />
  );
}

function InlineSelectEditor({ value, options, onCommit, onCancel }) {
  return (
    <select
      autoFocus
      value={value || ''}
      onChange={e => onCommit(e.target.value)}
      onBlur={onCancel}
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}
      style={styles.inlineInput}
    >
      <option value="">—</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function InlinePhoneEditor({ value, onChange, onCommit, onCancel }) {
  function onWrapBlur(e) {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    onCommit();
  }
  function onWrapKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
    else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  }
  return (
    <div
      className="mqm-inline-phone"
      onBlur={onWrapBlur}
      onKeyDown={onWrapKeyDown}
      style={{ flex: 1, minWidth: 0 }}
    >
      <PhoneInput
        international
        defaultCountry="PE"
        value={value || undefined}
        onChange={(v) => onChange(v || '')}
        numberInputProps={{ autoFocus: true, style: styles.inlineInput }}
      />
    </div>
  );
}

const styles = {
  // ---- Mobile/tablet-only overrides (merged only when useBreakpoint() says so) ----
  modalTablet: { maxWidth: 'calc(100vw - 32px)' },
  modalHeaderMobile: { padding: '12px 16px', borderBottom: '1px solid var(--border)', gap: 12 },
  formMobile: { padding: '16px 16px 0' },
  fieldRowMobile: { gridTemplateColumns: 'minmax(0, 1fr)' },
  modalActionsMobile: {
    margin: '8px -16px 0',
    padding: '12px 16px',
    borderTop: '1px solid var(--border)',
    flexWrap: 'wrap',
  },
  btnMobile: { minHeight: 44 },
  tapMin: { minHeight: 44 },
  containerTablet: { padding: '24px' },
  headerMobile: { flexDirection: 'column', gap: 6, marginBottom: 20 },
  titleMobile: { fontSize: 28 },
  textMobile: { fontSize: 15 },
  statsGridTablet: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 },
  statCardMobile: { padding: '16px', minWidth: 0 },
  statValueMobile: { fontSize: 30 },
  statLabelMobile: { fontSize: 13 },
  tableHeaderCompact: { padding: '16px 16px 12px', flexWrap: 'wrap', gap: 10 },
  searchRowMobile: { padding: '0 16px 14px', display: 'flex', gap: 8, alignItems: 'center' },
  searchWrapMobile: { maxWidth: 'none', flex: 1, minWidth: 0 },
  searchClearBtnTouch: { width: 44, height: 44, right: 0, borderRadius: 22, background: 'transparent', fontSize: 14 },
  filtersBtn: {
    minHeight: 44, padding: '0 16px', borderRadius: 8,
    border: '1px solid var(--border)', background: '#fff', color: 'var(--wine)',
    fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    flexShrink: 0,
  },
  sheetSectionLabel: {
    fontSize: 12, fontWeight: 700, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4,
  },
  sheetOption: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48,
    padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)',
    background: '#fff', color: 'var(--text-primary)', fontSize: 15, fontWeight: 500,
    textAlign: 'left', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
  },
  sheetOptionActive: { borderColor: '#6B1C2A', background: 'rgba(107,28,42,0.06)', color: '#6B1C2A', fontWeight: 600 },
  // Project cards (compact replacement for the projects table)
  cardList: { display: 'flex', flexDirection: 'column', gap: 12, padding: '0 16px 16px' },
  cardListTablet: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  projCard: {
    display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
    padding: 14, borderRadius: 12, border: '1px solid var(--border)',
    background: '#FDFBF8', cursor: 'pointer', boxSizing: 'border-box',
  },
  projCardActive: { borderColor: '#6B1C2A', background: 'rgba(107,28,42,0.06)' },
  projCardTop: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  projCardName: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', wordBreak: 'break-word', lineHeight: 1.3 },
  projCardClient: { fontSize: 15, color: 'var(--text-secondary)', marginTop: 2, wordBreak: 'break-word' },
  trashBtnTouch: { width: 44, height: 44, borderRadius: 10 },
  projCardBars: { display: 'flex', flexDirection: 'column', gap: 6 },
  projCardBarRow: { display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr) 42px', alignItems: 'center', gap: 8 },
  projCardBarLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  projCardBarPct: { fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  projCardTotal: { fontSize: 13, color: 'var(--text-primary)' },
  projCardPills: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  projCardPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44,
    padding: '0 14px', border: 'none', cursor: 'pointer', fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
  },
  projCardMeta: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 12px' },
  projCardMetaItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  projCardMetaLabel: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-light)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  projCardMetaValue: { fontSize: 15, color: 'var(--text-primary)', wordBreak: 'break-word' },
  projCardArqBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, maxWidth: '100%',
    padding: '0 10px 0 4px', margin: '0 0 0 -4px', borderRadius: 22,
    border: '1px solid var(--border)', background: '#fff', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
  },
  projCardArqName: { fontSize: 15, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  detailLineMobile: { gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, padding: '10px 14px' },
  inlineDisplayMobile: { fontSize: 15, minHeight: 44 },
  allTasksFiltersMobile: { padding: '12px 16px 0' },
  allTasksListMobile: { maxHeight: 'none', overflowY: 'visible' },
  sidePanelMobile: { width: '100%', maxWidth: '100%' },
  panelHeaderMobile: { padding: 'max(12px, env(safe-area-inset-top)) 16px 14px' },
  panelBodyMobile: { padding: '16px 16px 20px' },
  panelActionsMobile: { padding: '12px 16px max(12px, env(safe-area-inset-bottom))' },
  container: {
    padding: '32px 36px',
    // Full viewport width — the projects table needs the room now that diseño
    // and obra each have their own column.
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 32,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    marginTop: 4,
  },
  headerRight: {
    textAlign: 'right',
  },
  date: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    textTransform: 'capitalize',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 18,
    marginBottom: 28,
  },
  statCard: {
    background: '#fff',
    borderRadius: 14,
    padding: '22px 24px',
    border: '1px solid var(--border)',
  },
  statTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  statIcon: {
    fontSize: 20,
  },
  statChange: {
    fontSize: 11,
    color: 'var(--text-light)',
    fontWeight: 500,
  },
  statValue: {
    fontSize: 34,
    fontWeight: 700,
    fontFamily: "'Cormorant Garamond', serif",
    lineHeight: 1,
    marginBottom: 4,
    color: 'var(--text-primary)',
  },
  statLabel: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    fontWeight: 500,
    letterSpacing: '0.02em',
  },
  tableCard: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px 16px',
  },
  tableTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
  },
  tableBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--wine)',
    background: 'rgba(107,28,42,0.06)',
    padding: '4px 12px',
    borderRadius: 20,
  },
  addBtn: {
    padding: '8px 18px',
    borderRadius: 8,
    border: 'none',
    background: '#6B1C2A',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'background 0.2s',
  },
  sortToggleBtn: {
    padding: '6px 12px',
    borderRadius: 20,
    border: '1px solid var(--border)',
    background: '#fff',
    color: 'var(--wine)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  trashBtn: {
    width: 28,
    height: 28,
    borderRadius: 7,
    border: '1px solid rgba(184,59,59,0.25)',
    background: 'rgba(184,59,59,0.08)',
    color: '#B83B3B',
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dangerBtn: {
    padding: '9px 20px',
    borderRadius: 8,
    border: 'none',
    background: '#B83B3B',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  phoneRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
  },
  phoneCountryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 10px',
    borderRadius: '8px 0 0 8px',
    border: '1px solid var(--border)',
    borderRight: 'none',
    background: '#FDFBF8',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  phoneFlag: { fontSize: 16, lineHeight: 1 },
  phoneDial: { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  phoneCaret: { fontSize: 10, color: 'var(--text-light)', marginLeft: 2 },
  phoneNumberInput: {
    flex: 1,
    minWidth: 0,
    padding: '9px 12px',
    borderRadius: '0 8px 8px 0',
    border: '1px solid var(--border)',
    background: '#FDFBF8',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    letterSpacing: '0.02em',
    fontVariantNumeric: 'tabular-nums',
  },
  phoneDropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: 260,
    maxWidth: '100%',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
    zIndex: 2000,
    overflow: 'hidden',
  },
  phoneDropdownSearchWrap: {
    padding: 8,
    borderBottom: '1px solid var(--border)',
    background: '#FDFBF8',
  },
  phoneDropdownSearch: {
    width: '100%',
    padding: '7px 10px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: '#fff',
    fontSize: 12,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  phoneDropdownList: { maxHeight: 240, overflowY: 'auto' },
  phoneDropdownEmpty: {
    padding: '16px 12px', textAlign: 'center', color: 'var(--text-light)', fontSize: 12,
  },
  phoneDropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    textAlign: 'left',
  },
  phoneDropdownItemActive: { background: 'rgba(107,28,42,0.08)' },
  phoneDropdownName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  phoneDropdownDial: { color: 'var(--text-light)', fontVariantNumeric: 'tabular-nums' },

  searchRow: {
    padding: '0 24px 14px',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: 420,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--text-light)',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '9px 36px 9px 36px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: '#FDFBF8',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchClearBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 22,
    height: 22,
    borderRadius: 11,
    border: 'none',
    background: 'rgba(107,28,42,0.08)',
    color: '#6B1C2A',
    fontSize: 11,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'visible',
  },
  loadingMsg: {
    padding: '40px 24px',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-light)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
    fontFamily: "'DM Sans', sans-serif",
  },
  tr: {
    borderBottom: '1px solid #f3efe9',
  },
  trClickable: {
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  trActive: {
    background: 'rgba(107,28,42,0.06)',
    cursor: 'pointer',
  },
  td: {
    padding: '14px 12px',
    fontSize: 13,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  projectName: {
    display: 'block',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellTruncate: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  durationSingle: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  durationStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  durationLine: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  durationEmpty: {
    fontSize: 12,
    color: 'var(--text-light)',
  },
  progressCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  modalidadLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--wine)',
    letterSpacing: '0.02em',
  },
  phaseBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 5,
  },
  phaseBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    lineHeight: 1,
  },
  phaseDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  modalidadBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    marginTop: 6,
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(107,28,42,0.08)',
    border: '1px solid rgba(107,28,42,0.20)',
    color: 'var(--wine)',
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
  },
  splitProgressRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 34px',
    alignItems: 'center',
    gap: 6,
  },
  splitProgressLabel: {
    fontSize: 10.5,
    color: 'var(--text-secondary)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  splitProgressBar: {
    height: 5,
    background: '#F0EBE3',
    borderRadius: 3,
    overflow: 'hidden',
  },
  splitProgressPct: {
    fontSize: 10.5,
    color: 'var(--text-secondary)',
    fontWeight: 700,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  splitProgressTotal: {
    fontSize: 11,
    color: 'var(--text-primary)',
    marginTop: 2,
  },
  progressBar: {
    flex: 1,
    height: 6,
    background: '#F0EBE3',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.5s ease',
  },
  progressText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    minWidth: 32,
    textAlign: 'right',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  statusMenu: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 16,
    minWidth: 170,
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
    padding: 4,
    zIndex: 1500,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  statusMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    padding: '7px 10px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontFamily: "'DM Sans', sans-serif",
  },
  statusMenuItemActive: { background: 'rgba(107,28,42,0.06)' },

  arquitectaPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minWidth: 0,
    padding: '4px 10px 4px 4px',
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: '#FDFBF8',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
  },
  arquitectaName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  arquitectaEmpty: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--text-light)',
    padding: '0 4px',
  },
  arquitectaMenu: {
    position: 'fixed',
    minWidth: 240,
    maxHeight: 320,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 10,
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
    padding: 4,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  arquitectaMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    padding: '7px 10px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontFamily: "'DM Sans', sans-serif",
  },
  arquitectaMenuItemActive: { background: 'rgba(107,28,42,0.06)' },
  arquitectaMenuCargo: {
    fontSize: 10,
    fontWeight: 500,
    color: 'var(--text-light)',
    flexShrink: 0,
  },

  allTasksFilters: { display: 'flex', gap: 6, flexWrap: 'wrap', padding: '12px 28px 0' },
  allTasksFilterBtn: {
    padding: '6px 14px', borderRadius: 20, border: '1px solid var(--border)',
    background: '#fff', color: 'var(--text-secondary)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  allTasksFilterBtnActive: { borderColor: '#6B1C2A', background: '#6B1C2A', color: '#fff' },
  allTasksList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '55vh', overflowY: 'auto', paddingTop: 12 },
  allTasksRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
    background: '#FDFBF8', border: '1px solid var(--border)', borderRadius: 8,
    cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
  },
  allTasksName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  allTasksMeta: { fontSize: 11, color: 'var(--text-light)', marginTop: 2 },
  allTasksPill: { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, whiteSpace: 'nowrap' },
  allTasksPillDone: { background: '#4A7C59', color: '#fff' },
  allTasksPillProg: { background: '#C4922A', color: '#fff' },
  allTasksPillPend: { background: '#E8E2D9', color: '#6B6B6B' },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  // Modal styles
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(2px)',
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 580,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px 0',
  },
  modalTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 24,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    padding: '20px 28px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.02em',
  },
  input: {
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    background: '#FDFBF8',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  clientSuggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 30,
    overflow: 'hidden',
    maxHeight: 240,
    overflowY: 'auto',
  },
  clientSuggestionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    textAlign: 'left',
    padding: '8px 12px',
    border: 'none',
    borderBottom: '1px solid var(--border)',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  clientSuggestionName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  clientSuggestionMeta: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    fontFamily: "'DM Sans', sans-serif",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 8,
  },
  cancelBtn: {
    padding: '9px 20px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'transparent',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-secondary)',
  },
  submitBtn: {
    padding: '9px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#6B1C2A',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  secondaryBtn: {
    padding: '9px 20px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: '#fff',
    color: 'var(--wine)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  viewBtn: {
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid rgba(107,28,42,0.25)',
    background: 'rgba(107,28,42,0.06)',
    color: '#6B1C2A',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-light)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  sectionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  editLinkBtn: {
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid var(--border)',
    background: '#fff',
    color: 'var(--wine)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  panelBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.25)',
    backdropFilter: 'blur(1px)',
    zIndex: 900,
  },
  sidePanel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 420,
    maxWidth: '100vw',
    background: '#fff',
    boxShadow: '-12px 0 40px rgba(0,0,0,0.08)',
    zIndex: 901,
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideInRight 0.25s ease-out',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '22px 24px 18px',
    borderBottom: '1px solid var(--border)',
    gap: 12,
  },
  panelEyebrow: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: 'var(--text-light)',
    fontWeight: 700,
    marginBottom: 6,
  },
  panelTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 24,
    fontWeight: 600,
    color: 'var(--wine)',
    margin: 0,
    lineHeight: 1.2,
    wordBreak: 'break-word',
  },
  panelBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  panelActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '14px 24px 20px',
    borderTop: '1px solid var(--border)',
    background: '#FDFBF8',
  },
  ganttBtnRow: {
    display: 'flex',
    gap: 10,
    width: '100%',
  },
  ganttDesignBtn: {
    flex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#6B1C2A',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  ganttBuildBtn: {
    flex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#3E2118',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    whiteSpace: 'nowrap',
  },
  ganttEmoji: {
    fontSize: 14,
    lineHeight: 1,
  },
  detailList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    background: '#FDFBF8',
    border: '1px solid var(--border)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  detailLine: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderTop: '1px solid #f3efe9',
    fontSize: 13,
  },
  detailLineLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-light)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  detailLineValue: {
    fontSize: 13,
    color: 'var(--text-primary)',
    fontWeight: 500,
    wordBreak: 'break-word',
  },
  detailEmpty: {
    color: 'var(--text-light)',
    fontStyle: 'italic',
    fontWeight: 400,
  },
  inlineDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minHeight: 28,
    padding: '4px 8px',
    margin: '-4px -8px',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    color: 'var(--text-primary)',
    fontWeight: 500,
    transition: 'background 0.15s',
  },
  inlineStatus: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-light)',
    flexShrink: 0,
  },
  inlineEditorWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  inlineInput: {
    flex: 1,
    minWidth: 0,
    padding: '7px 10px',
    borderRadius: 7,
    border: '1px solid var(--wine)',
    background: '#fff',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    outline: 'none',
    boxSizing: 'border-box',
  },
  editGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  divider: {
    height: 1,
    background: 'var(--border)',
  },
  projectCard: {
    background: '#FDFBF8',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: 14,
  },
  projectCardName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--wine)',
    lineHeight: 1.2,
  },
  projectCardNameEditable: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    borderRadius: 6,
    padding: '2px 6px',
    margin: '-2px -6px',
    transition: 'background 0.15s',
  },
  projectCardNameInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--wine)',
    borderRadius: 6,
    padding: '2px 6px',
    margin: '-3px -7px',
    background: '#fff',
    outline: 'none',
  },
  projectCardMeta: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 4,
    textTransform: 'capitalize',
  },
  projectDates: {
    display: 'flex',
    gap: 16,
    marginTop: 12,
  },
  projectDateLabel: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--text-light)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 2,
  },
  projectDateValue: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 14,
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '10px 12px',
    background: '#FDFBF8',
    border: '1px solid var(--border)',
    borderRadius: 8,
  },
  detailLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--text-light)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  detailValue: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
};
